import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import { Card } from '../ui/Card';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import Modal from '../ui/Modal';
import Badge from '../ui/Badge';
import { blogService, BlogConnection } from '../../services/blogService';
import { marketplaceIntegrationService, MarketplaceConnection } from '../../services/marketplaceIntegrationService';
import { generateFluxSnippet, FLUX_FEATURES } from '../../utils/fluxSnippet';
import { Plus, Globe, Wifi, WifiOff, Edit2, Trash2, CheckCircle, XCircle, Loader2, KeyRound, Activity, ExternalLink, Zap, Copy } from 'lucide-react';

type ConnType = 'wordpress' | 'rest_api' | 'webhook' | 'devskin_marketplace';
type AuthType = 'app_password' | 'bearer' | 'api_key' | 'basic' | 'none';

interface FormState {
  name: string;
  type: ConnType;
  site_url: string;
  api_endpoint: string;
  auth_type: AuthType;
  username: string;
  credential: string;
  api_key_header: string;
  extra_headers: string;
  default_status: string;
  is_default: boolean;
  flux_enabled: boolean;
  flux_app_id: string;
  flux_api_key: string;
  marketplace_connection_id: string;
}

interface TestResult {
  ok: boolean;
  error?: string;
  user?: string;
}

const emptyForm: FormState = {
  name: '',
  type: 'wordpress',
  site_url: '',
  api_endpoint: '',
  auth_type: 'none',
  username: '',
  credential: '',
  api_key_header: '',
  extra_headers: '',
  default_status: 'publish',
  is_default: false,
  flux_enabled: false,
  flux_app_id: '',
  flux_api_key: '',
  marketplace_connection_id: ''
};

type TFunc = (key: string, fallback: string) => string;

const buildTypeOptions = (t: TFunc) => [
  { value: 'devskin_marketplace', label: t('blogSeo.connections.typeMarketplace', 'Blog da minha loja (Devskin Marketplace)') },
  { value: 'wordpress', label: t('blogSeo.connections.typeWordpress', 'WordPress') },
  { value: 'rest_api', label: t('blogSeo.connections.typeRestApi', 'API REST') },
  { value: 'webhook', label: t('blogSeo.connections.typeWebhook', 'Webhook') }
];

const buildAuthOptions = (t: TFunc) => [
  { value: 'none', label: t('blogSeo.connections.authNone', 'Nenhuma') },
  { value: 'bearer', label: t('blogSeo.connections.authBearer', 'Bearer token') },
  { value: 'api_key', label: t('blogSeo.connections.authApiKey', 'API Key') },
  { value: 'basic', label: t('blogSeo.connections.authBasic', 'Basic') }
];

const typeLabel = (type: ConnType, t: TFunc): string =>
  type === 'devskin_marketplace'
    ? t('blogSeo.connections.typeMarketplaceShort', 'Blog da loja')
    : type === 'wordpress'
    ? t('blogSeo.connections.typeWordpress', 'WordPress')
    : type === 'rest_api'
    ? t('blogSeo.connections.typeRestApi', 'API REST')
    : t('blogSeo.connections.typeWebhook', 'Webhook');

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string }> = ({
  checked,
  onChange,
  label
}) => (
  <label className="flex items-center gap-3 cursor-pointer select-none">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={
        'relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ' +
        (checked ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600')
      }
    >
      <span
        className={
          'inline-block h-4 w-4 transform rounded-full bg-white transition-transform ' +
          (checked ? 'translate-x-6' : 'translate-x-1')
        }
      />
    </button>
    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
  </label>
);

const BlogConnections: React.FC = () => {
  const { t } = useTranslation();
  const typeOptions = buildTypeOptions(t);
  const authOptions = buildAuthOptions(t);
  const [connections, setConnections] = useState<BlogConnection[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState<boolean>(false);
  const [testingModal, setTestingModal] = useState<boolean>(false);
  const [modalTestResult, setModalTestResult] = useState<TestResult | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [categories, setCategories] = useState<Record<string, { id: number | string; name: string; count: number }[]>>({});
  const [categoriesLoadingId, setCategoriesLoadingId] = useState<string | null>(null);
  const [mkpStores, setMkpStores] = useState<MarketplaceConnection[]>([]);
  const isMkp = form.type === 'devskin_marketplace';

  const loadConnections = async (): Promise<void> => {
    try {
      setLoading(true);
      const res = await blogService.listConnections();
      setConnections(res.data || []);
    } catch (err) {
      toast.error(t('blogSeo.connections.toastLoadError', 'Falha ao carregar conexões'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConnections();
    // Lojas já conectadas nas automações: dá para usar a mesma chave sem colar de novo
    marketplaceIntegrationService.list().then((r) => setMkpStores(r.data || [])).catch(() => setMkpStores([]));
  }, []);

  const updateForm = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const openCreate = (): void => {
    setEditingId(null);
    setForm(emptyForm);
    setModalTestResult(null);
    setModalOpen(true);
  };

  const openEdit = (conn: BlogConnection): void => {
    setEditingId(conn.id);
    setModalTestResult(null);
    setForm({
      name: conn.name || '',
      type: conn.type,
      site_url: conn.site_url || '',
      api_endpoint: conn.api_endpoint || '',
      auth_type: conn.auth_type || 'none',
      username: conn.username || '',
      credential: '',
      api_key_header: conn.api_key_header || '',
      extra_headers: conn.extra_headers ? JSON.stringify(conn.extra_headers, null, 2) : '',
      default_status: conn.default_status || 'publish',
      is_default: conn.is_default,
      flux_enabled: conn.flux_enabled,
      flux_app_id: conn.flux_app_id || '',
      flux_api_key: '',
      marketplace_connection_id: ''
    });
    setModalOpen(true);
  };

  /**
   * Builds the payload. Returns null (after toasting) if extra_headers JSON is invalid.
   * `forTest` keeps the credential even if blank handling differs — for test we still
   * send whatever the user typed (blank credential on edit means "use stored one" only
   * for actual saves; for tests with no id the backend needs the credential).
   */
  const buildPayload = (forTest: boolean): (Partial<BlogConnection> & { credential?: string; flux_api_key?: string }) | null => {
    let parsedHeaders: Record<string, string> | undefined;
    if (form.extra_headers.trim()) {
      try {
        const parsed = JSON.parse(form.extra_headers);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          parsedHeaders = parsed as Record<string, string>;
        } else {
          toast.error(t('blogSeo.connections.toastHeadersNotObject', 'Headers extras devem ser um objeto JSON'));
          return null;
        }
      } catch {
        toast.error(t('blogSeo.connections.toastHeadersInvalidJson', 'Headers extras: JSON inválido'));
        return null;
      }
    }

    if (form.type === 'devskin_marketplace') {
      // Só a chave (ou a loja já conectada): loja, site e blog vêm da API do marketplace
      const p: Partial<BlogConnection> & { credential?: string; marketplace_connection_id?: string } = {
        name: form.name.trim(),
        type: 'devskin_marketplace',
        default_status: form.default_status === 'draft' ? 'draft' : 'publish',
        is_default: form.is_default,
        flux_enabled: false
      };
      if (form.credential.trim()) p.credential = form.credential.trim();
      else if (form.marketplace_connection_id) p.marketplace_connection_id = form.marketplace_connection_id;
      return p;
    }

    const payload: Partial<BlogConnection> & { credential?: string; flux_api_key?: string } = {
      name: form.name.trim(),
      type: form.type,
      site_url: form.site_url.trim(),
      default_status: form.default_status.trim() || 'publish',
      is_default: form.is_default,
      flux_enabled: form.flux_enabled
    };

    if (form.type === 'wordpress') {
      payload.auth_type = 'app_password';
      if (form.username.trim()) payload.username = form.username.trim();
    } else {
      payload.api_endpoint = form.api_endpoint.trim() || null;
      payload.auth_type = form.auth_type;
      if (form.auth_type === 'api_key' && form.api_key_header.trim()) {
        payload.api_key_header = form.api_key_header.trim();
      }
      if (form.auth_type === 'basic' && form.username.trim()) {
        payload.username = form.username.trim();
      }
      if (parsedHeaders) payload.extra_headers = parsedHeaders;
    }

    // credential: only send when user typed something (blank on edit = keep existing).
    // On create or test, send whatever is typed.
    if (form.credential) {
      payload.credential = form.credential;
    } else if (forTest && !editingId) {
      // nothing to send
    }

    if (form.flux_enabled) {
      if (form.flux_app_id.trim()) payload.flux_app_id = form.flux_app_id.trim();
      if (form.flux_api_key) payload.flux_api_key = form.flux_api_key;
    }

    return payload;
  };

  const handleSave = async (): Promise<void> => {
    if (isMkp && !editingId && !form.credential.trim() && !form.marketplace_connection_id) {
      toast.error(t('blogSeo.connections.toastMkpKeyRequired', 'Cole a chave da loja ou escolha a loja já conectada'));
      return;
    }
    if (!form.name.trim() && !isMkp) {
      toast.error(t('blogSeo.connections.toastNameRequired', 'Informe um nome para a conexão'));
      return;
    }
    if (!form.site_url.trim() && !isMkp) {
      toast.error(t('blogSeo.connections.toastSiteUrlRequired', 'Informe a URL do site'));
      return;
    }
    const payload = buildPayload(false);
    if (!payload) return;

    try {
      setSaving(true);
      if (editingId) {
        await blogService.updateConnection(editingId, payload);
        toast.success(t('blogSeo.connections.toastUpdated', 'Conexão atualizada'));
      } else {
        const res = await blogService.createConnection(payload);
        const test: TestResult | undefined = res?.test;
        if (test && test.ok === false) {
          toast(
            t('blogSeo.connections.toastCreatedButCheckFailed', 'Conexão criada, mas a verificação falhou: {{error}}', {
              error: test.error || t('blogSeo.connections.unknownError', 'erro desconhecido')
            }),
            {
              icon: '⚠️'
            }
          );
        } else {
          toast.success(t('blogSeo.connections.toastCreatedAndVerified', 'Conexão criada e verificada'));
        }
      }
      setModalOpen(false);
      await loadConnections();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('blogSeo.connections.toastSaveError', 'Falha ao salvar conexão'));
    } finally {
      setSaving(false);
    }
  };

  const handleModalTest = async (): Promise<void> => {
    if (!form.site_url.trim() && !isMkp) {
      toast.error(t('blogSeo.connections.toastSiteUrlBeforeTest', 'Informe a URL do site antes de testar'));
      return;
    }
    const payload = buildPayload(true);
    if (!payload) return;
    try {
      setTestingModal(true);
      setModalTestResult(null);
      const res = await blogService.testConnection(payload);
      const result: TestResult = res?.data || {
        ok: false,
        error: t('blogSeo.connections.invalidResponse', 'Resposta inválida')
      };
      setModalTestResult(result);
      if (result.ok) {
        toast.success(
          result.user
            ? t('blogSeo.connections.toastConnectionOkUser', 'Conexão OK ({{user}})', { user: result.user })
            : t('blogSeo.connections.toastConnectionOk', 'Conexão OK')
        );
      } else {
        toast.error(
          t('blogSeo.connections.toastTestFailed', 'Falha: {{error}}', {
            error: result.error || t('blogSeo.connections.unknownError', 'erro desconhecido')
          })
        );
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || t('blogSeo.connections.toastTestError', 'Falha ao testar conexão');
      setModalTestResult({ ok: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTestingModal(false);
    }
  };

  const handleTestCard = async (conn: BlogConnection): Promise<void> => {
    try {
      setTestingId(conn.id);
      const res = await blogService.testConnection({ id: conn.id });
      const result: TestResult = res?.data || {
        ok: false,
        error: t('blogSeo.connections.invalidResponse', 'Resposta inválida')
      };
      setConnections((prev) =>
        prev.map((c) =>
          c.id === conn.id
            ? {
                ...c,
                status: result.ok ? 'connected' : 'error',
                last_error: result.ok ? null : result.error || c.last_error,
                last_checked_at: new Date().toISOString()
              }
            : c
        )
      );
      if (result.ok) {
        toast.success(
          result.user
            ? t('blogSeo.connections.toastConnectedUser', 'Conectado ({{user}})', { user: result.user })
            : t('blogSeo.connections.toastConnected', 'Conectado')
        );
      } else {
        toast.error(
          t('blogSeo.connections.toastCardError', 'Erro: {{error}}', {
            error: result.error || t('blogSeo.connections.checkFailed', 'falha na verificação')
          })
        );
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('blogSeo.connections.toastTestError', 'Falha ao testar conexão'));
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async (conn: BlogConnection): Promise<void> => {
    if (
      !window.confirm(
        t(
          'blogSeo.connections.confirmDelete',
          'Excluir a conexão "{{name}}"? Esta ação não pode ser desfeita.',
          { name: conn.name }
        )
      )
    ) {
      return;
    }
    try {
      await blogService.deleteConnection(conn.id);
      toast.success(t('blogSeo.connections.toastDeleted', 'Conexão excluída'));
      setConnections((prev) => prev.filter((c) => c.id !== conn.id));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('blogSeo.connections.toastDeleteError', 'Falha ao excluir conexão'));
    }
  };

  const handleLoadCategories = async (conn: BlogConnection): Promise<void> => {
    try {
      setCategoriesLoadingId(conn.id);
      const res = await blogService.getCategories(conn.id);
      setCategories((prev) => ({ ...prev, [conn.id]: res.data || [] }));
      if (!res.data || res.data.length === 0) {
        toast(t('blogSeo.connections.toastNoCategories', 'Nenhuma categoria encontrada'), { icon: 'ℹ️' });
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('blogSeo.connections.toastCategoriesError', 'Falha ao buscar categorias'));
    } finally {
      setCategoriesLoadingId(null);
    }
  };

  const renderStatusPill = (conn: BlogConnection): React.ReactNode => {
    if (conn.status === 'connected') {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
          <Wifi className="h-3.5 w-3.5" /> {t('blogSeo.connections.statusConnected', 'Conectado')}
        </span>
      );
    }
    if (conn.status === 'error') {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
          <WifiOff className="h-3.5 w-3.5" /> {t('blogSeo.connections.statusError', 'Erro')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
        <WifiOff className="h-3.5 w-3.5" /> {t('blogSeo.connections.statusDisconnected', 'Desconectado')}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('blogSeo.connections.title', 'Conexões de blog')}</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('blogSeo.connections.subtitle', 'Conecte seu blog (WordPress ou API externa) para publicar e agendar artigos.')}
          </p>
        </div>
        <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />} onClick={openCreate}>
          {t('blogSeo.connections.newConnection', 'Nova conexão')}
        </Button>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-500 dark:text-gray-400">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> {t('blogSeo.connections.loading', 'Carregando conexões...')}
        </div>
      ) : connections.length === 0 ? (
        <Card className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 dark:bg-gray-800/50">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
              <Globe className="h-8 w-8 text-primary-600 dark:text-primary-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('blogSeo.connections.emptyTitle', 'Nenhuma conexão ainda')}</h3>
            <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
              {t('blogSeo.connections.emptyDescription', 'Conecte seu WordPress ou uma API externa para começar a publicar e agendar artigos.')}
            </p>
            <Button className="mt-5" variant="primary" leftIcon={<Plus className="h-4 w-4" />} onClick={openCreate}>
              {t('blogSeo.connections.createFirst', 'Criar primeira conexão')}
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {connections.map((conn) => (
            <div
              key={conn.id}
              className="rounded-xl border border-gray-200 bg-white p-5 transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800/50"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate text-base font-semibold text-gray-900 dark:text-white">{conn.name}</h3>
                    <Badge variant="gray">{typeLabel(conn.type, t)}</Badge>
                    {conn.is_default && (
                      <Badge variant="primary">
                        <span className="mr-1">★</span> {t('blogSeo.connections.badgeDefault', 'Padrão')}
                      </Badge>
                    )}
                    {conn.flux_enabled && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                        <Activity className="h-3 w-3" /> {t('blogSeo.connections.badgeFluxActive', 'Flux ativo')}
                      </span>
                    )}
                  </div>
                  {conn.site_url && (
                    <a
                      href={conn.site_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
                    >
                      <Globe className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="truncate">{conn.site_url}</span>
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    </a>
                  )}
                </div>
                {renderStatusPill(conn)}
              </div>

              {conn.last_error && (
                <p className="mt-3 flex items-start gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-400">
                  <XCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span className="break-words">{conn.last_error}</span>
                </p>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                {conn.has_credential && (
                  <span className="inline-flex items-center gap-1">
                    <KeyRound className="h-3 w-3" /> {t('blogSeo.connections.credentialSaved', 'Credencial salva')}
                  </span>
                )}
                {conn.last_checked_at && (
                  <span>
                    {t('blogSeo.connections.checkedAt', 'Verificado em {{date}}', {
                      date: new Date(conn.last_checked_at).toLocaleString('pt-BR')
                    })}
                  </span>
                )}
              </div>

              {/* Categories (wordpress) */}
              {(conn.type === 'wordpress' || conn.type === 'devskin_marketplace') && categories[conn.id] && categories[conn.id].length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {categories[conn.id].map((cat) => (
                    <Badge key={cat.id} variant="gray" size="sm">
                      {cat.name} ({cat.count})
                    </Badge>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
                <Button
                  variant="secondary"
                  size="sm"
                  isLoading={testingId === conn.id}
                  leftIcon={<Wifi className="h-3.5 w-3.5" />}
                  onClick={() => handleTestCard(conn)}
                >
                  {t('blogSeo.connections.actionTest', 'Testar')}
                </Button>
                {(conn.type === 'wordpress' || conn.type === 'devskin_marketplace') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    isLoading={categoriesLoadingId === conn.id}
                    leftIcon={<CheckCircle className="h-3.5 w-3.5" />}
                    onClick={() => handleLoadCategories(conn)}
                  >
                    {t('blogSeo.connections.actionViewCategories', 'Ver categorias')}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Edit2 className="h-3.5 w-3.5" />}
                  onClick={() => openEdit(conn)}
                >
                  {t('blogSeo.connections.actionEdit', 'Editar')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20"
                  leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                  onClick={() => handleDelete(conn)}
                >
                  {t('blogSeo.connections.actionDelete', 'Excluir')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? t('blogSeo.connections.modalTitleEdit', 'Editar conexão') : t('blogSeo.connections.modalTitleNew', 'Nova conexão')}
        size="2xl"
        maxHeight
      >
        <div className="space-y-4">
          <Input
            label={t('blogSeo.connections.fieldName', 'Nome')}
            required={!isMkp}
            placeholder={isMkp ? t('blogSeo.connections.fieldNameMkpPlaceholder', 'Deixe vazio: usamos "Blog da <sua loja>"') : t('blogSeo.connections.fieldNamePlaceholder', 'Meu blog')}
            value={form.name}
            onChange={(e) => updateForm('name', e.target.value)}
          />

          <Select
            label={t('blogSeo.connections.fieldType', 'Tipo')}
            options={typeOptions}
            value={form.type}
            onChange={(e) => updateForm('type', e.target.value as ConnType)}
          />

          {isMkp && (
            <div className="space-y-3 rounded-xl border border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-800 dark:bg-indigo-900/20" data-testid="blog-mkp-form">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t('blogSeo.connections.mkpHelp', 'Os posts vão para o blog do site da sua loja (seusite/blog). No marketplace, crie uma chave em Configurações › API Keys e cole aqui.')}
              </p>
              {mkpStores.length > 0 && !form.credential && (
                <Select
                  label={t('blogSeo.connections.mkpUseStore', 'Usar a loja já conectada')}
                  options={[
                    { value: '', label: t('blogSeo.connections.mkpChoose', 'Escolha...') },
                    ...mkpStores.map((m) => ({ value: m.id, label: m.store_name || m.store_id }))
                  ]}
                  value={form.marketplace_connection_id}
                  onChange={(e) => updateForm('marketplace_connection_id', e.target.value)}
                />
              )}
              {!form.marketplace_connection_id && (
                <Input
                  label={t('blogSeo.connections.mkpKey', 'Chave da loja')}
                  type="password"
                  placeholder="dsk_..."
                  helperText={editingId ? t('blogSeo.connections.fieldCredentialHelp', 'Em edição, deixe vazio para manter a atual.') : undefined}
                  value={form.credential}
                  onChange={(e) => updateForm('credential', e.target.value)}
                />
              )}
              <Select
                label={t('blogSeo.connections.mkpStatus', 'Quando publicar, o post...')}
                options={[
                  { value: 'publish', label: t('blogSeo.connections.mkpPublish', 'Aparece no site na hora') },
                  { value: 'draft', label: t('blogSeo.connections.mkpDraft', 'Fica como rascunho na loja') }
                ]}
                value={form.default_status === 'draft' ? 'draft' : 'publish'}
                onChange={(e) => updateForm('default_status', e.target.value)}
              />
            </div>
          )}

          {!isMkp && (
          <Input
            label={t('blogSeo.connections.fieldSiteUrl', 'URL do site')}
            required
            placeholder={t('blogSeo.connections.fieldSiteUrlPlaceholder', 'https://meublog.com')}
            helperText={form.type === 'wordpress' ? t('blogSeo.connections.fieldSiteUrlHelp', 'URL base do site WordPress') : undefined}
            value={form.site_url}
            onChange={(e) => updateForm('site_url', e.target.value)}
          />
          )}

          {/* WordPress-specific */}
          {form.type === 'wordpress' && (
            <>
              <Input
                label={t('blogSeo.connections.fieldWpUser', 'Usuário WordPress')}
                value={form.username}
                onChange={(e) => updateForm('username', e.target.value)}
              />
              <Input
                label={t('blogSeo.connections.fieldAppPassword', 'Application Password')}
                type="password"
                helperText={t('blogSeo.connections.fieldAppPasswordHelp', 'Gere em WP Admin → Usuários → Perfil → Senhas de aplicativo. Cole aqui (formato: xxxx xxxx xxxx xxxx). Em edição, deixe vazio para manter a atual.')}
                value={form.credential}
                onChange={(e) => updateForm('credential', e.target.value)}
              />
            </>
          )}

          {/* REST API / Webhook-specific */}
          {(form.type === 'rest_api' || form.type === 'webhook') && (
            <>
              <Input
                label={t('blogSeo.connections.fieldApiEndpoint', 'Endpoint da API')}
                placeholder={t('blogSeo.connections.fieldApiEndpointPlaceholder', 'https://api.exemplo.com/posts')}
                value={form.api_endpoint}
                onChange={(e) => updateForm('api_endpoint', e.target.value)}
              />
              <Select
                label={t('blogSeo.connections.fieldAuth', 'Autenticação')}
                options={authOptions}
                value={form.auth_type}
                onChange={(e) => updateForm('auth_type', e.target.value as AuthType)}
              />
              {form.auth_type === 'api_key' && (
                <Input
                  label={t('blogSeo.connections.fieldHeaderName', 'Nome do header')}
                  placeholder={t('blogSeo.connections.fieldHeaderNamePlaceholder', 'X-API-Key')}
                  value={form.api_key_header}
                  onChange={(e) => updateForm('api_key_header', e.target.value)}
                />
              )}
              {form.auth_type !== 'none' && (
                <Input
                  label={t('blogSeo.connections.fieldCredential', 'Token / Senha / API Key')}
                  type="password"
                  helperText={editingId ? t('blogSeo.connections.fieldCredentialHelp', 'Em edição, deixe vazio para manter a atual.') : undefined}
                  value={form.credential}
                  onChange={(e) => updateForm('credential', e.target.value)}
                />
              )}
              {form.auth_type === 'basic' && (
                <Input
                  label={t('blogSeo.connections.fieldUser', 'Usuário')}
                  value={form.username}
                  onChange={(e) => updateForm('username', e.target.value)}
                />
              )}
              <Textarea
                label={t('blogSeo.connections.fieldExtraHeaders', 'Headers extras (JSON)')}
                placeholder='{"X-Custom":"valor"}'
                value={form.extra_headers}
                onChange={(e) => updateForm('extra_headers', e.target.value)}
              />
            </>
          )}

          {!isMkp && (
          <Input
            label={t('blogSeo.connections.fieldDefaultStatus', 'Status de publicação padrão')}
            helperText={t('blogSeo.connections.fieldDefaultStatusHelp', 'Para WordPress: publish, draft, pending...')}
            value={form.default_status}
            onChange={(e) => updateForm('default_status', e.target.value)}
          />
          )}

          <Toggle
            checked={form.is_default}
            onChange={(v) => updateForm('is_default', v)}
            label={t('blogSeo.connections.toggleSetDefault', 'Definir como conexão padrão')}
          />

          {/* Flux / Analytics section */}
          {!isMkp && (
          <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
            {/* Gradient hero */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-600 p-5">
              <div className="relative z-10 flex items-start gap-4">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                  <Zap className="h-6 w-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h4 className="text-lg font-bold text-white">{t('blogSeo.connections.fluxTitle', 'Flux by DevSkin')}</h4>
                  <p className="text-sm font-medium text-white/80">{t('blogSeo.connections.fluxTagline', 'Analytics, acessos e origens de tráfego')}</p>
                  <p className="mt-2 text-sm text-white/70">
                    {t('blogSeo.connections.fluxDescription', 'Acompanhe acessos, páginas mais vistas, origens de tráfego, heatmaps e gravação de sessões no seu blog.')}
                  </p>
                  <a
                    href="https://flux.devskin.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-white underline-offset-2 hover:underline"
                  >
                    {t('blogSeo.connections.fluxLearnMore', 'Saiba mais sobre o Flux')}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-4">
              {/* Enable toggle row */}
              <div className="flex items-start justify-between gap-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('blogSeo.connections.fluxEnableTitle', 'Ativar Flux Analytics')}</p>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {t('blogSeo.connections.fluxEnableDescription', 'Cole o script no seu blog para começar a coletar dados.')}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.flux_enabled}
                  onClick={() => updateForm('flux_enabled', !form.flux_enabled)}
                  className={
                    'relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ' +
                    (form.flux_enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600')
                  }
                >
                  <span
                    className={
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform ' +
                      (form.flux_enabled ? 'translate-x-6' : 'translate-x-1')
                    }
                  />
                </button>
              </div>

              {form.flux_enabled && (
                <div className="space-y-4">
                  <Input
                    label={t('blogSeo.connections.fluxAppId', 'App ID')}
                    className="font-mono"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={form.flux_app_id}
                    onChange={(e) => updateForm('flux_app_id', e.target.value)}
                  />
                  <Input
                    label={t('blogSeo.connections.fluxApiKey', 'API Key')}
                    type="password"
                    className="font-mono"
                    placeholder="dsk_pub_..."
                    helperText={t('blogSeo.connections.fluxApiKeyHelp', 'Encontre em flux.devskin.com → Configurações. Em edição, deixe vazio para manter a atual.')}
                    value={form.flux_api_key}
                    onChange={(e) => updateForm('flux_api_key', e.target.value)}
                  />

                  {/* Features chips */}
                  <div className="flex flex-wrap gap-2">
                    {FLUX_FEATURES.map((feature) => (
                      <span
                        key={feature.key}
                        className="inline-flex items-center gap-1.5 rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-300"
                      >
                        <Activity className="h-3 w-3" />
                        {feature.label}
                      </span>
                    ))}
                  </div>

                  {/* Install snippet */}
                  {form.flux_app_id && form.flux_api_key && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('blogSeo.connections.snippetLabel', 'Instale no <head> do seu blog')}
                        </label>
                        <Button
                          variant="secondary"
                          size="sm"
                          leftIcon={<Copy className="h-3.5 w-3.5" />}
                          onClick={() => {
                            navigator.clipboard.writeText(
                              generateFluxSnippet(form.flux_api_key, form.flux_app_id)
                            );
                            toast.success(t('blogSeo.connections.toastSnippetCopied', 'Snippet copiado!'));
                          }}
                        >
                          {t('blogSeo.connections.actionCopy', 'Copiar')}
                        </Button>
                      </div>
                      <pre className="overflow-x-auto whitespace-pre-wrap rounded-lg bg-gray-900 p-3 text-xs font-mono text-gray-300">
                        {generateFluxSnippet(form.flux_api_key, form.flux_app_id)}
                      </pre>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('blogSeo.connections.snippetNote', 'O Flux é client-side: cole este script no tema do seu blog (header). Não há ativação por API.')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          )}

          {/* Inline test result */}
          {modalTestResult && (
            <div
              className={
                'flex items-start gap-2 rounded-lg px-3 py-2 text-sm ' +
                (modalTestResult.ok
                  ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                  : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400')
              }
            >
              {modalTestResult.ok ? (
                <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              ) : (
                <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              )}
              <span className="break-words">
                {modalTestResult.ok
                  ? modalTestResult.user
                    ? t('blogSeo.connections.testResultOkUser', 'Conexão verificada com sucesso ({{user}})', { user: modalTestResult.user })
                    : t('blogSeo.connections.testResultOk', 'Conexão verificada com sucesso')
                  : t('blogSeo.connections.testResultFail', 'Falha na verificação: {{error}}', {
                      error: modalTestResult.error || t('blogSeo.connections.unknownError', 'erro desconhecido')
                    })}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
          <Button
            variant="secondary"
            isLoading={testingModal}
            leftIcon={<Wifi className="h-4 w-4" />}
            onClick={handleModalTest}
          >
            {t('blogSeo.connections.footerTest', 'Testar conexão')}
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setModalOpen(false)}>
              {t('blogSeo.connections.footerCancel', 'Cancelar')}
            </Button>
            <Button variant="primary" isLoading={saving} onClick={handleSave}>
              {t('blogSeo.connections.footerSave', 'Salvar')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BlogConnections;
